import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, ButtonIcon, Icon, Typography } from '@saviynt/design-system';

interface PlaylistSession {
  id: string;
  title: string;
  meta: string;
}

interface AuditLogViewerVideoPlaylistProps {
  sessions: PlaylistSession[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  ariaPrevLabel: string;
  ariaNextLabel: string;
}

/**
 * AuditLogViewerVideoPlaylist
 *
 * Reusable horizontal playlist carousel for session recordings.
 * Expects external CSS classes that already exist in the codebase.
 */
export default function AuditLogViewerVideoPlaylist({
  sessions,
  selectedIndex,
  onSelect,
  ariaPrevLabel = 'Previous',
  ariaNextLabel = 'Next',
}: AuditLogViewerVideoPlaylistProps): React.ReactElement {
  const playlistRef = useRef<HTMLDivElement | null>(null);
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(true);

  const updateNavVisibility = useCallback(() => {
    const el = playlistRef.current;

    if (!el) {
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth - 1;

    setShowLeftNav(el.scrollLeft > 0);
    setShowRightNav(el.scrollLeft < maxScroll);
  }, []);

  const handleScroll = useCallback(() => {
    updateNavVisibility();
  }, [updateNavVisibility]);

  // Keep the selected chip in view when selection changes (e.g., programmatic select)
  useEffect(() => {
    const el = playlistRef.current;

    if (!el) {
      return;
    }

    const child = el.children[selectedIndex] as HTMLElement | undefined;

    if (child) {
      el.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
      requestAnimationFrame(updateNavVisibility);
    }
  }, [selectedIndex, updateNavVisibility, sessions.length]);

  const scrollToNeighbor = useCallback(
    (direction: 'left' | 'right') => {
      const el = playlistRef.current;

      if (!el) {
        return;
      }

      const children = Array.from(el.children) as HTMLElement[];

      if (children.length === 0) {
        return;
      }

      const currentLeft = el.scrollLeft;
      let firstVisibleIdx = 0;

      for (let i = 0; i < children.length; i += 1) {
        if (children[i].offsetLeft >= currentLeft - 1) {
          firstVisibleIdx = i;
          break;
        }
      }

      const targetIdx =
        direction === 'right'
          ? Math.min(firstVisibleIdx + 1, children.length - 1)
          : Math.max(firstVisibleIdx - 1, 0);
      const target = children[targetIdx];

      el.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      requestAnimationFrame(updateNavVisibility);
    },
    [updateNavVisibility]
  );

  // Initialize nav visibility on mount and when content changes
  useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      updateNavVisibility();
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
    // Re-evaluate when number of sessions changes
  }, [updateNavVisibility, sessions.length]);

  return (
    <Box className='Sod-Recording-playlist-wrapper'>
      {showLeftNav && (
        <>
          <div
            className='Sod-Recording-playlist-fade-left'
            role='button'
            tabIndex={0}
            aria-label={ariaPrevLabel}
            onClick={() => scrollToNeighbor('left')}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollToNeighbor('left');
              }
            }}
          />
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={ariaPrevLabel}
            className='Sod-Recording-playlist-NavLeft'
            icon={<Icon kind='ChevronLeft' />}
            onClick={() => scrollToNeighbor('left')}
          />
        </>
      )}

      <Box className='Sod-Recording-playlist-Video-selector'>
        <div
          ref={playlistRef}
          className='Sod-Recording-playlist-Video-selector-frame'
          onScroll={handleScroll}>
          {sessions.map((s, i) => (
            <div
              key={s.id}
              role='button'
              tabIndex={0}
              className={
                i === selectedIndex
                  ? 'Sod-Recording-playlist-chip Sod-Recording-playlist-chip-isActive'
                  : 'Sod-Recording-playlist-chip'
              }
              onClick={() => onSelect(i)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(i);
                }
              }}>
              <Box className='Sod-Recording-playlist-chip-header'>
                <Typography kind='body2'>{s.title}</Typography>
              </Box>
              <Box className='Sod-Recording-playlist-chip-description'>
                <Typography kind='body2'>{s.meta}</Typography>
              </Box>
            </div>
          ))}
        </div>
      </Box>

      {showRightNav && (
        <>
          <div
            className='Sod-Recording-playlist-fade-right'
            role='button'
            tabIndex={0}
            aria-label={ariaNextLabel}
            onClick={() => scrollToNeighbor('right')}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollToNeighbor('right');
              }
            }}
          />
          <ButtonIcon
            size='medium'
            kind='ghost'
            ariaLabel={ariaNextLabel}
            className='Sod-Recording-playlist-NavRight'
            icon={<Icon kind='ChevronRight' />}
            onClick={() => scrollToNeighbor('right')}
          />
        </>
      )}
    </Box>
  );
}
